import { type GetHookResponse } from '../../hooks'
import { locationMapActivityFilterKey } from '../../keys'
import { useTantalimQuery } from '../../query'
import { findCachedDataForRegion } from './regionCacheUtils'
import { type LocationMapInputRegion } from './useLocationMapAttomFilter'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import {
  DAY,
  type LocationActivityFilter,
  type LocationActivityInput,
  type LocationActivityResponse,
  type LocationActivitySuccess,
  Logger,
  MINUTE,
} from '@terros/common'
import { useMemo } from 'react'

export type LocationMapActivityFilterInput = {
  region: LocationMapInputRegion
  filter?: LocationActivityFilter
}

const logger = new Logger('useLocationMapActivityFilter')
// logger.level = 'verbose'

export const useLocationMapActivityFilter = (
  input: LocationMapActivityFilterInput,
  enabled: boolean = true
): GetHookResponse<LocationActivityResponse> => {
  const { region, filter } = input
  logger.verbose('filter', filter)
  const queryClient = useQueryClient()

  const queryInput: LocationActivityInput = useMemo(
    () => ({
      top: region.topLeft,
      bottom: region.bottomRight,
      filter,
    }),
    [region, filter]
  )

  // Check if we can reuse cached data from a larger region
  const cachedData = useMemo(() => {
    if (!enabled) return undefined
    return findCachedDataForRegion(queryClient, queryInput)
  }, [queryClient, queryInput, enabled])

  const query = useTantalimQuery<LocationActivityInput, LocationActivitySuccess, LocationActivityResponse>({
    url: '/location/activity',
    queryKey: locationMapActivityFilterKey(queryInput),
    input: queryInput,
    convertResponse: (response) => {
      return response
    },
    options: {
      placeholderData: keepPreviousData,
      gcTime: DAY,
      staleTime: 30 * MINUTE,
      enabled: enabled && cachedData === undefined, // Disable API call if we have cached data
    },
  })

  return useMemo(() => {
    if (cachedData) {
      logger.verbose('reusing cached data from larger region')
      return {
        ...query,
        data: cachedData,
        isSuccess: true,
        isLoading: false,
        isFetching: false,
        error: undefined,
      }
    }
    return query
  }, [query, cachedData])
}
